/**
 * Site-wide Performance Manager
 * Handles loading indicators, memory management, error handling, and offline functionality
 */

class PerformanceManager {
  constructor() {
    this.loadingStates = new Map();
    this.errorStates = new Map();
    this.memoryThreshold = 100 * 1024 * 1024; // 100MB threshold
    this.offlineData = new Map();
    this.syncQueue = [];
    this.isOnline = navigator.onLine;
    
    // Initialize offline/online event listeners
    this.initializeOfflineSupport();
    
    // Initialize memory monitoring
    this.initializeMemoryMonitoring();
  }

  // Loading Progress Indicators
  trackLoadingStart(componentId, dataSize = 0) {
    const loadingState = {
      id: componentId,
      startTime: performance.now(),
      dataSize,
      progress: 0,
      isLoading: true
    };
    
    this.loadingStates.set(componentId, loadingState);
    this.dispatchLoadingEvent('loading-start', loadingState);
    
    return loadingState;
  }

  updateLoadingProgress(componentId, progress, message = '') {
    const loadingState = this.loadingStates.get(componentId);
    if (loadingState) {
      loadingState.progress = Math.min(100, Math.max(0, progress));
      loadingState.message = message;
      loadingState.lastUpdate = performance.now();
      
      this.dispatchLoadingEvent('loading-progress', loadingState);
    }
  }

  trackLoadingEnd(componentId, success = true, error = null) {
    const loadingState = this.loadingStates.get(componentId);
    if (loadingState) {
      loadingState.endTime = performance.now();
      loadingState.duration = loadingState.endTime - loadingState.startTime;
      loadingState.isLoading = false;
      loadingState.success = success;
      loadingState.error = error;
      
      this.dispatchLoadingEvent('loading-end', loadingState);
      
      // Clean up after a delay
      setTimeout(() => {
        this.loadingStates.delete(componentId);
      }, 5000);
    }
  }

  getLoadingState(componentId) {
    return this.loadingStates.get(componentId);
  }

  getAllLoadingStates() {
    return Array.from(this.loadingStates.values());
  }

  // Memory Management
  initializeMemoryMonitoring() {
    if ('memory' in performance) {
      // Monitor memory usage every 30 seconds
      setInterval(() => {
        this.checkMemoryUsage();
      }, 30000);
    }
  }

  getMemoryUsage() {
    if ('memory' in performance) {
      const memory = performance.memory;
      return {
        used: memory.usedJSHeapSize,
        total: memory.totalJSHeapSize,
        limit: memory.jsHeapSizeLimit,
        usedMB: (memory.usedJSHeapSize / 1048576).toFixed(2),
        totalMB: (memory.totalJSHeapSize / 1048576).toFixed(2),
        limitMB: (memory.jsHeapSizeLimit / 1048576).toFixed(2)
      };
    }
    return null;
  }

  isMemoryOptimal() {
    const memory = this.getMemoryUsage();
    if (!memory) return true; // Assume optimal if we can't measure
    
    return memory.used < this.memoryThreshold;
  }

  checkMemoryUsage() {
    const memory = this.getMemoryUsage();
    if (memory && memory.used > this.memoryThreshold) {
      console.warn('High memory usage detected:', memory);
      this.optimizeMemory();
    }
  }

  optimizeMemory() {
    // Clear old loading states
    const now = performance.now();
    for (const [id, state] of this.loadingStates.entries()) {
      if (!state.isLoading && (now - (state.endTime || state.startTime)) > 60000) {
        this.loadingStates.delete(id);
      }
    }

    // Clear old error states
    for (const [id, error] of this.errorStates.entries()) {
      if ((now - error.timestamp) > 300000) { // 5 minutes
        this.errorStates.delete(id);
      }
    }

    // Suggest garbage collection if available
    if (window.gc) {
      window.gc();
    }

    this.dispatchEvent('memory-optimized', { timestamp: now });
  }

  // Error Handling
  trackError(componentId, error, context = {}) {
    const errorState = {
      id: componentId,
      error: error instanceof Error ? error : new Error(error),
      context,
      timestamp: performance.now(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      stack: error instanceof Error ? error.stack : new Error().stack
    };

    this.errorStates.set(componentId, errorState);
    this.dispatchErrorEvent('error-tracked', errorState);

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Performance Manager - Error tracked:', errorState);
    }

    return errorState;
  }

  getError(componentId) {
    return this.errorStates.get(componentId);
  }

  clearError(componentId) {
    const cleared = this.errorStates.delete(componentId);
    if (cleared) {
      this.dispatchErrorEvent('error-cleared', { id: componentId });
    }
    return cleared;
  }

  getAllErrors() {
    return Array.from(this.errorStates.values());
  }

  // Offline Functionality
  initializeOfflineSupport() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.syncOfflineData();
      this.dispatchEvent('online', { timestamp: performance.now() });
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      this.dispatchEvent('offline', { timestamp: performance.now() });
    });
  }

  cacheData(key, data, ttl = 3600000) { // 1 hour default TTL
    const cacheEntry = {
      data,
      timestamp: Date.now(),
      ttl,
      expires: Date.now() + ttl
    };
    
    this.offlineData.set(key, cacheEntry);
    
    // Store in localStorage for persistence
    try {
      localStorage.setItem(`pm_cache_${key}`, JSON.stringify(cacheEntry));
    } catch (e) {
      console.warn('Failed to cache data to localStorage:', e);
    }
  }

  getCachedData(key) {
    let cacheEntry = this.offlineData.get(key);
    
    // Try localStorage if not in memory
    if (!cacheEntry) {
      try {
        const stored = localStorage.getItem(`pm_cache_${key}`);
        if (stored) {
          cacheEntry = JSON.parse(stored);
          this.offlineData.set(key, cacheEntry);
        }
      } catch (e) {
        console.warn('Failed to retrieve cached data from localStorage:', e);
      }
    }

    // Check if cache is still valid
    if (cacheEntry && Date.now() < cacheEntry.expires) {
      return cacheEntry.data;
    }

    // Clean up expired cache
    if (cacheEntry) {
      this.offlineData.delete(key);
      try {
        localStorage.removeItem(`pm_cache_${key}`);
      } catch (e) {
        // Ignore cleanup errors
      }
    }

    return null;
  }

  queueForSync(operation) {
    this.syncQueue.push({
      ...operation,
      timestamp: Date.now()
    });
  }

  syncOfflineData() {
    if (!this.isOnline || this.syncQueue.length === 0) {
      return;
    }

    console.log('Syncing offline data:', this.syncQueue.length, 'operations');
    
    // Process sync queue
    const operations = [...this.syncQueue];
    this.syncQueue = [];

    operations.forEach(async (operation) => {
      try {
        if (operation.handler && typeof operation.handler === 'function') {
          await operation.handler(operation.data);
        }
      } catch (error) {
        console.error('Failed to sync operation:', operation, error);
        // Re-queue failed operations
        this.syncQueue.push(operation);
      }
    });

    this.dispatchEvent('sync-completed', { 
      processed: operations.length,
      remaining: this.syncQueue.length 
    });
  }

  // Event System
  dispatchLoadingEvent(type, data) {
    this.dispatchEvent(`performance-${type}`, data);
  }

  dispatchErrorEvent(type, data) {
    this.dispatchEvent(`performance-${type}`, data);
  }

  dispatchEvent(type, data) {
    const event = new CustomEvent(type, { detail: data });
    window.dispatchEvent(event);
  }

  // Utility Methods
  formatDuration(ms) {
    if (ms < 1000) return `${Math.round(ms)}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
  }

  getPerformanceReport() {
    return {
      loading: {
        active: this.loadingStates.size,
        states: this.getAllLoadingStates()
      },
      errors: {
        count: this.errorStates.size,
        recent: this.getAllErrors().slice(-5)
      },
      memory: this.getMemoryUsage(),
      offline: {
        isOnline: this.isOnline,
        cachedItems: this.offlineData.size,
        syncQueue: this.syncQueue.length
      },
      timestamp: performance.now()
    };
  }
}

// Create singleton instance
const performanceManager = new PerformanceManager();

export default performanceManager;
export { PerformanceManager };